// src/lib/budgetMath.js
//
// One source of truth for budget math used by both Budget Builder and Reports.
// Step 1: Standalone utility (not yet imported anywhere).
// Step 2 (next): We will refactor BudgetBuilderManual.jsx to call these helpers,
//                so the numbers on Builder and Reports are guaranteed to match.
//
// ⚠️ Frequency rules for NON-normalized schedules depend on anchors (e.g., payDate).
//    This file includes a robust, conservative implementation that we will align
//    1:1 with your Builder logic in Step 2 (using the files you shared).
//
// Exports:
// - computeRowBudgetForMonth(row, yyyy, mm, opts?)         -> number
// - computeDocBudgetForMonth(budgetDoc, yyyy, mm, opts?)   -> Map key -> amount
// - computeBudgetForMonthList(budgetDocs, yyyy, mm, opts?) -> Map key -> amount
// - computeBudgetForRange(budgetDocs, startDate, endDate, opts?)
//     -> { months: string[], byMonth: { [YYYY-MM]: Map key->amount }, meta }
//
// Key = `${type}||${category}||${subcategory || ""}`
// Use parseKey(key) to read it back.

const clamp = (n) => (Number.isFinite(n) ? n : 0);
const toNum = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

export const toMonthKey = (y, m) =>
  `${y}-${String(m + 1).padStart(2, "0")}`; // m is 0-based

export const parseMonthKey = (key) => {
  const [Y, M] = String(key).split("-");
  return { y: +Y, m: +M - 1 };
};

export const makeKey = (type, category, subcategory = "") =>
  `${String(type)}||${String(category)}||${String(subcategory)}`;

export const parseKey = (key) => {
  const [type, category, subcategory] = String(key).split("||");
  return { type, category, subcategory };
};

// --- Month iteration helpers ---
const monthStart = (y, m) => new Date(y, m, 1);
const monthEnd = (y, m) => new Date(y, m + 1, 0); // last day
const monthsBetween = (startDate, endDate) => {
  const s = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const e = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const months = [];
  for (let y = s.getFullYear(), m = s.getMonth(); y < e.getFullYear() || (y === e.getFullYear() && m <= e.getMonth()); ) {
    months.push(toMonthKey(y, m));
    m++;
    if (m === 12) { m = 0; y++; }
  }
  return months;
};

// --- Frequency helpers ---
const weekdayIndex = (name) => {
  const map = { sun:0, mon:1, tue:2, wed:3, thu:4, fri:5, sat:6 };
  if (typeof name !== "string") return null;
  const k = name.trim().toLowerCase().slice(0,3);
  return map.hasOwnProperty(k) ? map[k] : null;
};

// Count occurrences of a schedule in a given month when normalize === false.
// ⚠️ We will align this with BudgetBuilderManual in Step 2 if any detail differs.
function occurrencesInMonth(frequency, payDate, y, m) {
  const start = monthStart(y, m);
  const end = monthEnd(y, m);

  const freq = String(frequency || "monthly").toLowerCase();

  // Helper: count specific weekday occurrences in a month
  const countWeekday = (w) => {
    let d = new Date(y, m, 1);
    let cnt = 0;
    while (d <= end) {
      if (d.getDay() === w) cnt++;
      d.setDate(d.getDate() + 1);
    }
    return cnt;
  };

  // Helper: bi-weekly stepping from an anchor date
  const countBiWeekly = (anchorDate) => {
    // If anchor missing, fall back to normalized bi-weekly pattern (26/12 ≈ 2.1667 per month).
    if (!(anchorDate instanceof Date) || Number.isNaN(anchorDate.getTime())) {
      return 0; // caller will handle fallback
    }
    let cnt = 0;
    // Step backwards to the first occurrence not after month end
    let d = new Date(anchorDate);
    // move d forward until it reaches current month window (to avoid long loops)
    while (d < start) d.setDate(d.getDate() + 14);
    while (d <= end) {
      cnt++;
      d.setDate(d.getDate() + 14);
    }
    return cnt;
  };

  // Try to parse payDate flexibly:
  // - ISO date "YYYY-MM-DD" -> Date
  // - "YYYY-MM" (we'll assume day 1)
  // - "DD" (day of month) -> Date(y,m,DD)
  // - weekday name -> weekday count in month
  let isoAnchor = null;
  let dayOfMonth = null;
  let weeklyIndex = weekdayIndex(payDate);
  if (typeof payDate === "string" && payDate) {
    const t = payDate.trim();
    // ISO date
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (iso) {
      isoAnchor = new Date(+iso[1], +iso[2]-1, +iso[3]);
    } else {
      const ym = /^(\d{4})-(\d{2})$/.exec(t);
      if (ym) {
        isoAnchor = new Date(+ym[1], +ym[2]-1, 1);
      } else {
        const dm = /^(\d{1,2})$/.exec(t);
        if (dm) dayOfMonth = Math.min(28, Math.max(1, +dm[1])); // cap to 28
      }
    }
  }

  if (freq === "weekly") {
    if (weeklyIndex != null) return countWeekday(weeklyIndex);
    // fallback: assume 4 or 5 weekly hits depending on month length
    // conservative: count of the weekday of the first day in month
    return countWeekday(start.getDay());
  }

  if (freq === "bi-weekly" || freq === "biweekly" || freq === "bi_weekly") {
    // Prefer anchor stepping; fallback handled by caller via normalization
    const cnt = countBiWeekly(isoAnchor || (dayOfMonth ? new Date(y, m, dayOfMonth) : null));
    return cnt;
  }

  if (freq === "twice-monthly" || freq === "twice_monthly" || freq === "semi-monthly" || freq === "semimonthly") {
    // Two occurrences per month; if two specific days are stored elsewhere we’ll plug them in Step 2.
    return 2;
  }

  if (freq === "monthly") {
    return 1;
  }

  if (freq === "quarterly") {
    // 1 occurrence when month aligns with anchor quarter if we have anchor; default to 1 in Mar/Jun/Sep/Dec
    if (isoAnchor instanceof Date && !Number.isNaN(isoAnchor.getTime())) {
      const qAnchor = Math.floor(isoAnchor.getMonth() / 3);
      const qThis   = Math.floor(m / 3);
      return qThis === qAnchor ? 1 : 0;
    }
    return [2,5,8,11].includes(m) ? 1 : 0; // Mar/Jun/Sep/Dec
  }

  // Default
  return 1;
}

// Convert base amount to a monthly equivalent when normalize === true.
function normalizedMonthlyAmount(amount, frequency) {
  const a = toNum(amount);
  const f = String(frequency || "monthly").toLowerCase();
  if (f === "weekly") return a * (52 / 12);
  if (f === "bi-weekly" || f === "biweekly" || f === "bi_weekly") return a * (26 / 12);
  if (f === "twice-monthly" || f === "twice_monthly" || f === "semi-monthly" || f === "semimonthly") return a * 2;
  if (f === "monthly") return a;
  if (f === "quarterly") return a / 3;
  // Fallback to monthly
  return a;
}

// --- Core: compute one subcategory row for a month ---
/**
 * row: {
 *   name: string,
 *   amount: number|string,
 *   frequency?: "weekly"|"bi-weekly"|"twice-monthly"|"monthly"|"quarterly",
 *   normalize?: boolean,
 *   payDate?: string,               // see parsing rules above
 *   customAmounts?: { [YYYY-MM]: number|string },
 *   goal?: {...}                    // ignored for math—type decides outflow/inflow
 * }
 */
export function computeRowBudgetForMonth(row = {}, yyyy, mm, opts = {}) {
  const {
    strictNonNormalizedFallbackToNormalize = true, // if no anchor info, fall back to normalized
  } = opts;

  const key = toMonthKey(yyyy, mm);
  // 1) Custom amount wins
  const custom = row?.customAmounts && Object.prototype.hasOwnProperty.call(row.customAmounts, key)
    ? toNum(row.customAmounts[key])
    : null;
  if (custom != null) return clamp(custom);

  const freq = row?.frequency ?? "monthly";
  const normalize = !!row?.normalize;

  // 2) Normalized monthly conversion
  if (normalize || row?.normalize === undefined) {
    return clamp(normalizedMonthlyAmount(row?.amount, freq));
  }

  // 3) Non-normalized: multiply base amount by count of occurrences this month
  // If we lack anchors, optionally fall back to normalized equivalent to avoid zeroing out budgets.
  const base = toNum(row?.amount);
  const count = occurrencesInMonth(freq, row?.payDate, yyyy, mm);
  if (count > 0) return clamp(base * count);

  if (strictNonNormalizedFallbackToNormalize) {
    return clamp(normalizedMonthlyAmount(base, freq));
  }
  // No occurrences; treat as 0 for this month
  return 0;
}

// --- Compute an entire budget document (category with subcategories) for a month ---
/**
 * budgetDoc: {
 *   category: string,
 *   type: "expense"|"income"|"savings"|"non-budget",
 *   subcategories: Array<row>
 * }
 * returns Map key -> amount  (key = `${type}||${category}||${subcategory}`)
 */
export function computeDocBudgetForMonth(budgetDoc = {}, yyyy, mm, opts = {}) {
  const out = new Map();
  const type = String(budgetDoc?.type || "").toLowerCase();
  if (type === "non-budget") return out; // excluded by design
  const category = budgetDoc?.category ?? "(Uncategorized)";
  const subs = Array.isArray(budgetDoc?.subcategories) ? budgetDoc.subcategories : [];

  for (const row of subs) {
    const sub = row?.name ?? "";
    const amt = computeRowBudgetForMonth(row, yyyy, mm, opts);
    if (!amt) continue;
    const k = makeKey(type, category, sub);
    out.set(k, (out.get(k) ?? 0) + amt);
  }
  return out;
}

// --- Compute many documents for a month -> combined Map ---
export function computeBudgetForMonthList(budgetDocs = [], yyyy, mm, opts = {}) {
  const out = new Map();
  for (const doc of budgetDocs) {
    const m = computeDocBudgetForMonth(doc, yyyy, mm, opts);
    for (const [k, v] of m.entries()) {
      out.set(k, (out.get(k) ?? 0) + v);
    }
  }
  return out;
}

// --- Compute across a range of months ---
/**
 * Returns:
 * {
 *   months: [ "YYYY-MM", ... ],
 *   byMonth: { [YYYY-MM]: Map key->amount },
 *   meta: { usedFallbackForNonNormalized?: boolean }
 * }
 */
export function computeBudgetForRange(budgetDocs = [], startDate, endDate, opts = {}) {
  const months = monthsBetween(startDate, endDate);
  const byMonth = {};
  for (const key of months) {
    const { y, m } = parseMonthKey(key);
    byMonth[key] = computeBudgetForMonthList(budgetDocs, y, m, opts);
  }
  return { months, byMonth, meta: {} };
}
